import { type ColumnConfig } from '@/components/datatable/dataTableTypes';
import type { SelectModel } from '@/components/forms/FormSelect.vue';

export const isDateString = (value: string): boolean => {
  // Regular expression to match ISO 8601 date format (with and without time)
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z)?$/;

  // Check if the string matches the ISO 8601 date format
  if (!isoDateRegex.test(value)) {
    return false;
  }

  // Try parsing the string as a date and check if it's valid
  const date = new Date(value);
  return !isNaN(date.getTime());
};

export function formatDate(
  date: string | Date | null | undefined,
  locale: string,
  dateType?: ColumnConfig['type'],
): string {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  let formatted;

  switch (dateType) {
    case 'datetime':
      // Format as localized date  time
      formatted = dateObj.toLocaleString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      return locale.startsWith('de') ? `${formatted} Uhr` : formatted;
    case 'time':
      // Format as localized time only
      formatted = dateObj.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      });
      return locale.startsWith('de') ? `${formatted} Uhr` : formatted;
    default:
      // Default: date only
      formatted = dateObj.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return formatted;
  }
}

// converts a date in localized Format to ISO-String
export const parseDateFromIso = (localizedDateString: string, _locale: string): string | null => {
  const date = new Date(localizedDateString);
  return !isNaN(date.getTime()) ? date.toISOString() : null;
};

export function isToday(dateInput: string | Date): boolean {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return false;

  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

export function isSameWeek(date: string | Date, referenceDate: Date = new Date()): boolean {
  const inputDate = typeof date === 'string' ? new Date(date) : date;
  const ref = new Date(referenceDate);

  // reset to monday
  const startOfWeek = new Date(ref);
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(ref.getDate() - ref.getDay() + 1);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return inputDate >= startOfWeek && inputDate <= endOfWeek;
}

export const parseLocalizedDateToIsoString = (
  localizedDateString: string,
  locale: string,
): string | null => {
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const parts = formatter.formatToParts(new Date());
  const regexParts = parts.map((part) => {
    switch (part.type) {
      case 'year':
        return '(?<year>\\d{4})';
      case 'month':
        return '(?<month>[\\w]+)';
      case 'day':
        return '(?<day>\\d{1,2})';
      default:
        return part.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape non-literal parts
    }
  });

  const dateRegex = new RegExp(`^${regexParts.join('')}$`, 'i');
  const match = dateRegex.exec(localizedDateString);
  if (!match || !match.groups) return null;

  const { year, month, day } = match.groups;
  const monthIndex = new Date(`${year}-${month}-01`).getMonth();

  const newDate = new Date(parseInt(String(year)), monthIndex, parseInt(String(day)));

  const isoString = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];

  return String(isoString);
};

export const convertISOTo_yy_mm_dd = (isoString: string): string => {
  const date = new Date(isoString);

  const year = String(date.getFullYear()).slice(-2);

  const month = String(date.getMonth() + 1).padStart(2, '0');

  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export function parseISODate(dateString: string | undefined): Date | null {
  return dateString ? new Date(dateString) : null;
}

export function labourDays(startDatum: Date, endDatum: Date): number {
  let labourDays = 0;
  const actualDate = new Date(startDatum);

  while (actualDate <= endDatum) {
    const tag = actualDate.getDay();
    // check if day is labour day (monday to friday)
    if (tag !== 0 && tag !== 6) {
      labourDays++;
    }
    // switch to next day
    actualDate.setDate(actualDate.getDate() + 1);
  }

  return labourDays;
}

export function days(startDatum: Date, endDatum: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  const start = new Date(startDatum).setHours(0, 0, 0, 0);
  const end = new Date(endDatum).setHours(0, 0, 0, 0);

  const diffMs = end - start;
  return diffMs >= 0 ? Math.floor(diffMs / msPerDay) + 1 : 0;
}

export const isDateStringOverdue = (dateString: string) => {
  let referenceDate = new Date();

  const chainableMethods = {
    comparedTo: function (otherDate: string) {
      referenceDate = new Date(otherDate);
      return this;
    },
    workdays: function (laborDaysThreshold: number) {
      const entryDay = new Date(dateString);
      const passedLaborDays = labourDays(entryDay, referenceDate);
      return passedLaborDays > laborDaysThreshold;
    },
    days: function (daysThreshold: number) {
      const entryDay = new Date(dateString);
      const passeDays = days(entryDay, referenceDate);
      return passeDays > daysThreshold;
    },
    differenceInWorkdays: function (threshold = 0) {
      const entryDay = new Date(dateString);
      return labourDays(entryDay, referenceDate) - threshold;
    },
  };

  return chainableMethods;
};

export function getTranslatedMonths(t: (key: string) => string): SelectModel[] {
  return Array.from({ length: 12 }, (_, i) => ({
    id: i + 1,
    name: t(`months.${i + 1}`),
  }));
}

export function addYear(date: Date, yearsToAdd: number): Date {
  const newDate = new Date(date.getTime());
  newDate.setFullYear(newDate.getFullYear() + yearsToAdd);
  return newDate;
}

type DateInput = string | number | Date | null | undefined;
export const asDate = (v: DateInput): Date | undefined => {
  if (v == null || v === '') return undefined;
  return v instanceof Date ? v : new Date(v);
};

/** Safely parse ISO date (YYYY-MM-DD) into Date or null */
export function parseIsoDate(dateString?: string | null): Date | null {
  if (!dateString) return null;
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? null : d;
}

/** Format Date to ISO string (YYYY-MM-DD) */
export function formatIsoDate(date: Date): string {
  return String(date.toISOString().split('T')[0]);
}

/**
 * Calculates the next period range based on a given end date.
 * @param periodEnd - ISO string of the previous period end date (e.g. '2024-12-31')
 * @param months - Number of months the new period should span (default: 12)
 * @returns Object containing nextStart and nextEnd as ISO strings (YYYY-MM-DD)
 */
export function getNextPeriod(
  periodEnd?: string | null,
  months = 12,
): { nextStart?: string; nextEnd?: string } {
  if (!periodEnd) return {};

  const prevEnd = new Date(periodEnd);
  if (isNaN(prevEnd.getTime())) return {};

  // Start is one day after previous end
  const nextStart = new Date(prevEnd);
  nextStart.setDate(nextStart.getDate() + 1);

  // End = start + N months - 1 day
  const nextEnd = new Date(nextStart);
  nextEnd.setMonth(nextEnd.getMonth() + months);
  nextEnd.setDate(nextEnd.getDate() - 1);

  return {
    nextStart: nextStart.toISOString().split('T')[0],
    nextEnd: nextEnd.toISOString().split('T')[0],
  };
}

/**
 * Returns the same calendar day in a future year (default: +1 year).
 *
 * Example:
 *   getSameDayNextYear('2024-11-29') → '2025-11-29'
 *   getSameDayNextYear('2020-02-29') → '2021-02-28' (handles leap years safely)
 */
export function getSameDayNextYear(isoDate: string, yearsToAdd = 1): string | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;

  const newDate = new Date(d);
  newDate.setFullYear(d.getFullYear() + yearsToAdd);

  // Handle leap year case: if month changed, set to last day of previous month
  if (newDate.getMonth() !== d.getMonth()) {
    newDate.setDate(0); // llast day of previous month
  }

  return String(newDate.toISOString().split('T')[0]);
}

// dateUtils.ts (Erweiterung)

// Parsed short localized date string (e.g. `31.01.25`) to ISO (YYYY-MM-DD)
// Supports dd.MM.yy and dd.MM.yyyy for German locale.
export function parseShortLocaleDateToIsoString(
  localizedDateString: string,
  locale: string,
  options?: {
    /** pivot rule for 2-digit years; default 70 → 00-69 = 2000-2069, 70-99 = 1970-1999 */
    shortYearPivot?: number;
  },
): string | null {
  const trimmed = localizedDateString?.trim();
  if (!trimmed) return null;

  // aktuell nur de explizit
  if (locale.startsWith('de')) {
    return parseShortDeDateToIsoString(trimmed, options);
  }

  return null;
}

function parseShortDeDateToIsoString(
  input: string,
  options?: {
    shortYearPivot?: number;
  },
): string | null {
  // Supports: 1.2.25, 01.02.25, 01.02.2025
  const match = input.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/);
  if (!match) return null;

  const [, dStr, mStr, yStr] = match;
  const day = Number(dStr);
  const month = Number(mStr);
  let year = Number(yStr);

  if (yStr?.length === 2) {
    const pivot = options?.shortYearPivot ?? 70;
    year = year >= pivot ? 1900 + year : 2000 + year;
  }

  const date = new Date(year, month - 1, day);

  // Validity check (no automatic "correction" like 31.02. → 03.03.)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  // Auf reines Datum (ohne Zeit) normalisieren
  const iso = date.toISOString().split('T')[0]; // YYYY-MM-DD
  return String(iso);
}

// Parse backend UTC ISO string like "2025-01-01T12:00:00.000Z" to a local date-only
export function parseBackendUtcDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;

  // Strip time part, keep only the local date (year, month, day)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Convert a local date-only (year, month, day) to a UTC ISO string at noon
export function dateToSafeUtcIso(date: Date): string {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();

  // 12:00 local time; toISOString() will convert to UTC
  // → stays on the same calendar date in UTC for Europe/Berlin
  const localNoon = new Date(y, m, d, 12, 0, 0, 0);
  return localNoon.toISOString();
}

// For dirty-check: normalize any backend ISO string to "YYYY-MM-DD"
export function normalizeBackendIsoToDateOnlyString(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export // Convert Date to "YYYY-MM-DD" using local date parts
function dateToDateOnlyString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
